import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatMoney } from "@/lib/api";
import { ChartBar, Users, Briefcase, FileText, Globe, Stack, MapPin, Tag, ChatCircle, Pencil, Trash, Plus, Receipt, EnvelopeSimple, Palette, Storefront, Newspaper, CreditCard, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useSettings } from "@/lib/settings";

export function AdminLayout() {
  const { user, loading } = useAuth();
  const settings = useSettings();
  if (loading) return <div className="py-32 text-center">Loading…</div>;
  if (!user || user.role === "client") return <Navigate to="/login" replace />;

  const NAV = [
    { to: "/admin", icon: ChartBar, label: "Overview", end: true },
    { to: "/admin/leads", icon: Users, label: "Leads (CRM)" },
    { to: "/admin/franchise", icon: Storefront, label: "Franchise" },
    { to: "/admin/franchise-opps", icon: Storefront, label: "Opportunities" },
    { to: "/admin/payments", icon: CreditCard, label: "Payments" },
    { to: "/admin/marketing", icon: ChartBar, label: "Marketing" },
    { to: "/admin/projects", icon: Briefcase, label: "Projects" },
    { to: "/admin/invoices", icon: FileText, label: "Invoices" },
    { to: "/admin/proposals", icon: Receipt, label: "Proposals" },
    { to: "/admin/countries", icon: Globe, label: "Countries" },
    { to: "/admin/services", icon: Stack, label: "Services & Pricing" },
    { to: "/admin/case-studies", icon: MapPin, label: "Case Studies" },
    { to: "/admin/blog", icon: Newspaper, label: "Blog" },
    { to: "/admin/users", icon: Users, label: "Users" },
    { to: "/admin/email-log", icon: EnvelopeSimple, label: "Email Log" },
    { to: "/admin/branding", icon: Palette, label: "Branding" },
  ];

  return (
    <div data-testid="admin-layout" className="fade-in py-12 min-h-screen" style={{ background: "var(--gos-bone)" }}>
      <div className="container-gos">
        <div className="overline mb-2">Admin panel</div>
        <h1 className="font-display text-3xl text-gos-charcoal tracking-tight">{settings.brand_name}</h1>
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10 mt-10">
          <aside className="space-y-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 text-sm rounded-sm ${isActive ? "bg-gos-navy text-white" : "text-gos-slate hover:bg-gos-subtle"}`
                }
              >
                <n.icon size={16} /> {n.label}
              </NavLink>
            ))}
          </aside>
          <div><Outlet /></div>
        </div>
      </div>
    </div>
  );
}

export function AdminOverview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <div>Loading…</div>;
  return (
    <div data-testid="admin-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Stat label="Total leads" value={stats.leads.total} />
        <Stat label="New leads" value={stats.leads.new} />
        <Stat label="Won deals" value={stats.leads.won} />
        <Stat label="Active projects" value={stats.projects} />
        <Stat label="Active countries" value={stats.countries} />
        <Stat label="Active services" value={stats.services} />
        <Stat label="Invoices" value={stats.invoices.total} />
        <Stat label="Paid invoices" value={stats.invoices.paid} />
      </div>
      <div>
        <div className="overline mb-3">Revenue by currency</div>
        <div className="bg-white border border-gos-border p-5 rounded-sm">
          {Object.keys(stats.revenue_by_currency).length === 0 ? (
            <div className="text-sm text-gos-muted">No paid invoices yet.</div>
          ) : (
            <div className="space-y-2 font-mono text-sm">
              {Object.entries(stats.revenue_by_currency).map(([ccy, amount]) => (
                <div key={ccy} className="flex justify-between"><span>{ccy}</span><span>{formatMoney(amount, ccy)}</span></div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const LEAD_STATUSES = ["new", "contacted", "proposal_sent", "negotiation", "won", "lost"];

export function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");
  const refresh = () => api.get(`/admin/leads${filter ? `?status=${filter}` : ""}`).then((r) => setLeads(r.data));
  useEffect(() => { refresh(); }, [filter]);

  const updateStatus = async (id, status) => {
    await api.put(`/admin/leads/${id}`, { status });
    toast.success("Status updated");
    refresh();
  };

  return (
    <div data-testid="admin-leads">
      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setFilter("")} className={`px-3 py-1 text-xs rounded-sm border ${!filter ? "bg-gos-navy text-white border-gos-navy" : "border-gos-border"}`}>All</button>
        {LEAD_STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`lead-filter-${s}`}
            className={`px-3 py-1 text-xs rounded-sm border uppercase tracking-widest ${filter === s ? "bg-gos-navy text-white border-gos-navy" : "border-gos-border"}`}>{s.replace("_", " ")}</button>
        ))}
      </div>

      <div className="bg-white border border-gos-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gos-subtle text-xs uppercase tracking-widest text-gos-navy">
            <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Target</th><th className="text-left p-3">Services</th><th className="text-left p-3">Status</th><th className="text-left p-3">Created</th></tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-gos-border">
                <td className="p-3">
                  <div className="text-gos-charcoal">{l.full_name}</div>
                  <div className="text-xs text-gos-muted">{l.company || ""}</div>
                </td>
                <td className="p-3 text-gos-slate">{l.email}</td>
                <td className="p-3 text-gos-slate">{l.target_country}</td>
                <td className="p-3 text-xs text-gos-slate max-w-xs truncate">{l.services?.join(", ")}</td>
                <td className="p-3">
                  <select value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`}
                    className="border border-gos-border rounded-sm px-2 py-1 text-xs">
                    {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                </td>
                <td className="p-3 text-xs text-gos-muted">{l.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-gos-muted">No leads</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminCountries() {
  const [countries, setCountries] = useState([]);
  const [editing, setEditing] = useState(null);
  const refresh = () => api.get("/countries?active_only=false").then((r) => setCountries(r.data));
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    const payload = { ...editing };
    delete payload.id;
    try {
      if (editing.id) await api.put(`/admin/countries/${editing.id}`, payload);
      else await api.post("/admin/countries", payload);
      toast.success("Saved");
      setEditing(null); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this country?")) return;
    await api.delete(`/admin/countries/${id}`);
    refresh();
  };

  return (
    <div data-testid="admin-countries">
      <div className="flex justify-between mb-6">
        <div className="overline">{countries.length} countries</div>
        <div className="flex gap-2">
          <Link to="/admin/country-wizard" data-testid="open-wizard-btn" className="btn-secondary text-xs !py-2">
            <Sparkle size={14} weight="bold" /> Quick add (wizard)
          </Link>
          <button onClick={() => setEditing({ slug: "", name: "", flag: "🌐", currency_code: "USD", currency_symbol: "$", active: true, display_order: countries.length + 1, recommended_industries: [] })}
            data-testid="add-country-btn" className="btn-primary text-xs !py-2"><Plus size={14} weight="bold" /> New country</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {countries.map((c) => (
          <div key={c.id} className="bg-white border border-gos-border p-4 rounded-sm flex justify-between items-start gap-3">
            <div>
              <div className="flex items-center gap-2"><span className="text-2xl">{c.flag}</span><span className="font-display text-lg text-gos-navy">{c.name}</span></div>
              <div className="text-xs text-gos-muted mt-1">{c.slug} · {c.currency_code} · order {c.display_order} · {c.active ? "active" : "inactive"}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(c)} data-testid={`edit-country-${c.slug}`} className="text-gos-navy hover:text-gos-sand"><Pencil size={16} /></button>
              <button onClick={() => del(c.id)} data-testid={`del-country-${c.slug}`} className="text-gos-error hover:text-gos-sand"><Trash size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div data-testid="country-edit-modal" className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6 overflow-auto">
          <div className="bg-white rounded-sm border border-gos-border max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="overline mb-2">{editing.id ? "Edit country" : "New country"}</div>
            <h3 className="font-display text-2xl mb-4">{editing.name || "Untitled"}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ["slug","Slug"],["name","Name"],["flag","Flag emoji"],["currency_code","Currency code"],
                ["currency_symbol","Currency symbol"],["capital_city","Capital"],["language","Language"],["timezone","Timezone"],
                ["tagline","Tagline"],["display_order","Display order (number)"],
                ["setup_timeline_days","Setup timeline (days)"],["corporate_tax_rate","Corp tax %"],["personal_tax_rate","Personal tax %"],
                ["min_capital_usd","Min capital USD"],["banking_difficulty","Banking difficulty (1-10)"],
                ["visa_availability","Visa availability (1-10)"],["ease_of_doing_business","Ease of business (1-10)"],
                ["startup_ecosystem","Startup ecosystem (1-10)"],["tech_sector","Tech sector (1-10)"],
                ["import_export","Import/Export (1-10)"],["manufacturing","Manufacturing (1-10)"],
                ["government_incentives","Govt incentives (1-10)"],["immigration_pathways","Immigration (1-10)"],
                ["overall_rating","Overall rating (1-10)"],
                ["short_description","Short description"],["tile_image_url","Tile image URL"],["hero_image_url","Hero image URL"],
              ].map(([k, label]) => (
                <div key={k}>
                  <label className="block text-[10px] uppercase tracking-widest text-gos-muted mb-1">{label}</label>
                  <input value={editing[k] ?? ""} onChange={(e) => setEditing({ ...editing, [k]: typeof editing[k] === "number" ? Number(e.target.value) : e.target.value })}
                    className="w-full border border-gos-border rounded-sm px-2 py-1.5 text-sm" />
                </div>
              ))}
              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-widest text-gos-muted mb-1">Long description</label>
                <textarea value={editing.long_description ?? ""} onChange={(e) => setEditing({ ...editing, long_description: e.target.value })}
                  rows={3} className="w-full border border-gos-border rounded-sm px-2 py-1.5 text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-widest text-gos-muted mb-1">Recommended industries (comma-separated)</label>
                <input value={(editing.recommended_industries || []).join(", ")} onChange={(e) => setEditing({ ...editing, recommended_industries: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  className="w-full border border-gos-border rounded-sm px-2 py-1.5 text-sm" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active
              </label>
            </div>
            <div className="flex gap-2 mt-6 justify-end">
              <button onClick={() => setEditing(null)} className="btn-secondary text-xs !py-2">Cancel</button>
              <button onClick={save} data-testid="country-save-btn" className="btn-primary text-xs !py-2">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminServices() {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const refresh = () => api.get("/services").then((r) => setServices(r.data));
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    const payload = { ...editing };
    delete payload.id;
    try {
      if (editing.id) await api.put(`/admin/services/${editing.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success("Saved");
      setEditing(null); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    await api.delete(`/admin/services/${id}`);
    refresh();
  };

  const updatePrice = (ccy_slug, field, val) => {
    const prices = (editing.prices || []).map((p) => p.country_slug === ccy_slug ? { ...p, [field]: Number(val) } : p);
    setEditing({ ...editing, prices });
  };

  return (
    <div data-testid="admin-services">
      <div className="flex justify-between mb-6">
        <div className="overline">{services.length} services</div>
        <button onClick={() => setEditing({ slug: "", name: "", category: "business_setup", description: "", icon: "Briefcase", active: true, display_order: services.length + 1, prices: [] })} data-testid="add-service-btn" className="btn-primary text-xs !py-2"><Plus size={14} weight="bold" /> New service</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {services.map((s) => (
          <div key={s.id} className="bg-white border border-gos-border p-4 rounded-sm flex justify-between items-start gap-3">
            <div>
              <div className="font-display text-lg text-gos-navy">{s.name}</div>
              <div className="text-xs text-gos-muted mt-1">{s.category} · {s.prices?.length || 0} prices</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(s)} data-testid={`edit-service-${s.slug}`} className="text-gos-navy hover:text-gos-sand"><Pencil size={16} /></button>
              <button onClick={() => del(s.id)} className="text-gos-error hover:text-gos-sand"><Trash size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div data-testid="service-edit-modal" className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6 overflow-auto">
          <div className="bg-white rounded-sm border border-gos-border max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="overline mb-2">{editing.id ? "Edit service" : "New service"}</div>
            <h3 className="font-display text-2xl mb-4">{editing.name || "Untitled"}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ["slug","Slug"],["name","Name"],["icon","Icon (phosphor name)"],["display_order","Display order"],
              ].map(([k, l]) => (
                <div key={k}>
                  <label className="block text-[10px] uppercase tracking-widest text-gos-muted mb-1">{l}</label>
                  <input value={editing[k] ?? ""} onChange={(e) => setEditing({ ...editing, [k]: typeof editing[k] === "number" ? Number(e.target.value) : e.target.value })}
                    className="w-full border border-gos-border rounded-sm px-2 py-1.5 text-sm" />
                </div>
              ))}
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-gos-muted mb-1">Category</label>
                <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full border border-gos-border rounded-sm px-2 py-1.5 text-sm bg-white">
                  <option value="business_setup">Business Setup</option>
                  <option value="business_presence">Business Presence</option>
                  <option value="business_growth">Business Growth</option>
                  <option value="investment_expansion">Investment & Expansion</option>
                  <option value="professional_referrals">Professional Referrals</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active
              </label>
              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-widest text-gos-muted mb-1">Description</label>
                <textarea value={editing.description ?? ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  rows={2} className="w-full border border-gos-border rounded-sm px-2 py-1.5 text-sm" />
              </div>
            </div>
            <div className="mt-6">
              <div className="overline mb-2">Country pricing</div>
              <div className="space-y-2">
                {(editing.prices || []).map((p) => (
                  <div key={p.country_slug} className="grid grid-cols-4 gap-2 items-center text-xs">
                    <div className="font-mono text-gos-slate">{p.country_slug} ({p.currency})</div>
                    <input type="number" value={p.one_time} onChange={(e) => updatePrice(p.country_slug, "one_time", e.target.value)} className="border border-gos-border rounded-sm px-2 py-1" placeholder="One-time" data-testid={`price-onetime-${p.country_slug}`} />
                    <input type="number" value={p.monthly} onChange={(e) => updatePrice(p.country_slug, "monthly", e.target.value)} className="border border-gos-border rounded-sm px-2 py-1" placeholder="Monthly" data-testid={`price-monthly-${p.country_slug}`} />
                    <input value={p.currency} onChange={(e) => { const prices = editing.prices.map((pp) => pp.country_slug === p.country_slug ? { ...pp, currency: e.target.value } : pp); setEditing({ ...editing, prices }); }} className="border border-gos-border rounded-sm px-2 py-1" />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2 mt-6 justify-end">
              <button onClick={() => setEditing(null)} className="btn-secondary text-xs !py-2">Cancel</button>
              <button onClick={save} data-testid="service-save-btn" className="btn-primary text-xs !py-2">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminProjects() {
  const [projects, setProjects] = useState([]);
  useEffect(() => { api.get("/admin/projects").then((r) => setProjects(r.data)); }, []);
  return (
    <div data-testid="admin-projects">
      <div className="overline mb-3">Projects</div>
      <div className="space-y-2">
        {projects.map((p) => (
          <div key={p.id} className="bg-white border border-gos-border p-4 rounded-sm">
            <div className="flex justify-between"><div className="font-display text-lg text-gos-navy">{p.title}</div><span className="text-xs uppercase tracking-widest">{p.status}</span></div>
            <div className="text-xs text-gos-muted mt-1">{p.target_country} · {p.progress_percent}% complete</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  useEffect(() => { api.get("/admin/invoices").then((r) => setInvoices(r.data)); }, []);
  return (
    <div data-testid="admin-invoices">
      <div className="overline mb-3">Invoices</div>
      <div className="space-y-2">
        {invoices.map((i) => (
          <div key={i.id} className="bg-white border border-gos-border p-4 rounded-sm flex justify-between">
            <div>
              <div className="font-mono text-sm">{i.number}</div>
              <div className="text-xs text-gos-muted">{i.due_date}</div>
            </div>
            <div className="text-right">
              <div className="font-display text-lg text-gos-navy">{formatMoney(i.total, i.currency)}</div>
              <span className="text-[10px] uppercase tracking-widest text-gos-sand">{i.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminCaseStudies() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/case-studies").then((r) => setItems(r.data)); }, []);
  return (
    <div data-testid="admin-case-studies">
      <div className="overline mb-3">Case studies</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((cs) => (
          <Link to={`/case-studies/${cs.slug}`} key={cs.id} className="bg-white border border-gos-border p-4 rounded-sm">
            <div className="font-display text-lg text-gos-navy">{cs.title}</div>
            <div className="text-xs text-gos-muted">{cs.client_origin} → {cs.target_country}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function AdminUsers() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)); }, []);
  return (
    <div data-testid="admin-users">
      <div className="overline mb-3">Users ({users.length})</div>
      <div className="bg-white border border-gos-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gos-subtle text-xs uppercase tracking-widest"><tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Role</th><th className="text-left p-3">Company</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-gos-border"><td className="p-3">{u.full_name}</td><td className="p-3 text-gos-slate">{u.email}</td><td className="p-3"><span className="text-xs uppercase tracking-widest">{u.role}</span></td><td className="p-3 text-gos-slate">{u.company || "—"}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-gos-border p-5 rounded-sm">
      <div className="overline !text-gos-muted">{label}</div>
      <div className="font-display text-3xl text-gos-navy mt-2">{value}</div>
    </div>
  );
}

export function AdminProposals() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/proposals").then((r) => setItems(r.data)); }, []);
  return (
    <div data-testid="admin-proposals">
      <div className="overline mb-3">Proposals</div>
      <div className="bg-white border border-gos-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gos-subtle text-xs uppercase tracking-widest"><tr><th className="text-left p-3">Number</th><th className="text-left p-3">Client</th><th className="text-left p-3">Country</th><th className="text-left p-3">Total</th><th className="text-left p-3">Valid until</th><th className="text-left p-3">PDF</th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-gos-border">
                <td className="p-3 font-mono">{p.number}</td>
                <td className="p-3">{p.client?.full_name} <span className="text-xs text-gos-muted">{p.client?.email}</span></td>
                <td className="p-3">{p.country_name}</td>
                <td className="p-3 font-mono">{formatMoney(p.grand_total, p.currency)}</td>
                <td className="p-3 text-xs text-gos-muted">{p.valid_until}</td>
                <td className="p-3"><a href={`${process.env.REACT_APP_BACKEND_URL}/api/proposals/${p.id}/pdf`} target="_blank" rel="noreferrer" className="text-gos-navy underline">Download</a></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-gos-muted">No proposals yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminEmailLog() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/email-log").then((r) => setItems(r.data)); }, []);
  return (
    <div data-testid="admin-email-log">
      <div className="overline mb-3">Email log ({items.length})</div>
      <p className="text-xs text-gos-muted mb-4">
        Emails are currently <span className="text-gos-sand font-medium">MOCKED</span> (logged here, not delivered). Add a <code className="bg-gos-subtle px-1">RESEND_API_KEY</code> and verified <code className="bg-gos-subtle px-1">EMAIL_FROM</code> to switch on live delivery.
      </p>
      <div className="space-y-2">
        {items.map((e) => (
          <div key={e.id} className="bg-white border border-gos-border p-4 rounded-sm" data-testid={`email-row-${e.id}`}>
            <div className="flex justify-between items-start mb-1">
              <div>
                <span className="overline mr-2">{e.template}</span>
                <span className="text-xs text-gos-muted">{e.sent_at?.slice(0, 19)?.replace("T", " ")}</span>
              </div>
              <span className={`text-[10px] uppercase tracking-widest ${e.mocked ? "text-gos-sand" : "text-gos-success"}`}>{e.mocked ? "mocked" : "sent"}</span>
            </div>
            <div className="text-sm text-gos-charcoal font-medium">{e.subject}</div>
            <div className="text-xs text-gos-muted mt-1">To: {e.to}</div>
            <pre className="text-xs text-gos-slate mt-2 whitespace-pre-wrap font-sans">{e.body}</pre>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-gos-muted">No emails yet.</div>}
      </div>
    </div>
  );
}
